import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { formatFCFA, formatNumber } from "../lib/fcfa";
import { Users, Shield, TrendingUp, TrendingDown, Wallet, Activity, Briefcase, CalendarDays } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";

const STATUS_COLORS = ["#2E7D32", "#F57F17", "#C62828"];
const PIE_COLORS = ["#C62828", "#2E7D32", "#FBC02D", "#1976D2", "#7B1FA2", "#F57F17", "#00ACC1"];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
    api.get("/finance/summary").then(({ data }) => setSummary(data));
  }, []);

  if (!stats) return <div className="text-[#616161]">Chargement...</div>;

  const statusData = [
    { name: "Actifs", value: stats.actifs },
    { name: "Blessés", value: stats.blesses },
    { name: "Suspendus", value: stats.suspendus },
  ].filter(d => d.value > 0);

  return (
    <div>
      <PageHeader
        overline="Tableau de bord"
        title={`Bienvenue, ${user?.label || "Coach"}`}
        description="Vue d'ensemble en temps réel de votre club."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Joueurs" value={formatNumber(stats.players_count)} sub={`${stats.actifs} actifs`} testid="stat-players" color="#C62828" />
        <StatCard icon={Shield} label="Équipes" value={formatNumber(stats.teams_count)} sub="catégories" testid="stat-teams" color="#1976D2" />
        <StatCard icon={Briefcase} label="Employés" value={formatNumber(stats.employees_count || 0)} sub="staff" testid="stat-employees" color="#7B1FA2" />
        <StatCard icon={Wallet} label="Solde" value={formatFCFA(stats.solde)} sub={stats.solde >= 0 ? "Positif" : "Négatif"} testid="stat-balance" color={stats.solde >= 0 ? "#2E7D32" : "#C62828"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 bg-white border border-[#E0E0E0] rounded-md p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-xs tracking-overline text-[#616161]">Finances</p>
              <h3 className="font-display text-xl font-bold mt-1">Évolution mensuelle</h3>
            </div>
            <div className="text-right text-sm">
              <p className="text-[#2E7D32] flex items-center gap-1 justify-end"><TrendingUp className="w-4 h-4" />Recettes: {formatFCFA(stats.total_recette)}</p>
              <p className="text-[#C62828] flex items-center gap-1 justify-end mt-1"><TrendingDown className="w-4 h-4" />Dépenses: {formatFCFA(stats.total_depense)}</p>
            </div>
          </div>
          <div className="h-72" data-testid="chart-finance">
            {summary?.monthly?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis dataKey="month" stroke="#616161" fontSize={12} />
                  <YAxis stroke="#616161" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatFCFA(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="recette" stroke="#2E7D32" strokeWidth={2.5} name="Recettes" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="depense" stroke="#C62828" strokeWidth={2.5} name="Dépenses" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart text="Aucune transaction enregistrée pour l'instant." />
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E0E0E0] rounded-md p-6">
          <p className="text-xs tracking-overline text-[#616161]">Effectif</p>
          <h3 className="font-display text-xl font-bold mt-1 mb-4">Statut joueurs</h3>
          <div className="h-72" data-testid="chart-status">
            {statusData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {statusData.map((_, i) => <Cell key={i} fill={STATUS_COLORS[i]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart text="Aucun joueur." />
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E0E0E0] rounded-md p-6">
        <p className="text-xs tracking-overline text-[#616161]">Effectif</p>
        <h3 className="font-display text-xl font-bold mt-1 mb-4">Répartition par poste</h3>
        <div className="h-72" data-testid="chart-positions">
          {stats.by_position?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.by_position}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                <XAxis dataKey="name" stroke="#616161" fontSize={12} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis stroke="#616161" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#C62828" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart text="Ajoutez des joueurs pour voir la répartition." />
          )}
        </div>
      </div>

      {stats.upcoming_events?.length > 0 && (
        <div className="bg-white border border-[#E0E0E0] rounded-md p-6 mt-4" data-testid="dashboard-upcoming">
          <p className="text-xs tracking-overline text-[#616161]">Agenda</p>
          <h3 className="font-display text-xl font-bold mt-1 mb-4 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#C62828]" /> Prochains événements (14 jours)
          </h3>
          <div className="space-y-2">
            {stats.upcoming_events.slice(0, 5).map(e => (
              <div key={e.id} className="flex items-center gap-4 py-2 border-b border-[#E0E0E0] last:border-0">
                <div className="text-center w-16">
                  <p className="text-[10px] tracking-overline text-[#616161]">{new Date(e.start_date).toLocaleString("fr-FR", { month: "short" })}</p>
                  <p className="font-display font-bold text-2xl text-[#C62828]">{new Date(e.start_date).getDate()}</p>
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{e.title}</p>
                  <p className="text-xs text-[#616161] capitalize">{e.type}{e.location ? ` · ${e.location}` : ""}{e.opponent ? ` · vs ${e.opponent}` : ""}</p>
                </div>
                <span className="text-xs text-[#616161]">{new Date(e.start_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid, color }) {
  return (
    <div className="stat-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-overline text-[#616161]">{label}</p>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-display text-3xl font-extrabold tracking-tight">{value}</p>
      <p className="text-xs text-[#616161]">{sub}</p>
    </div>
  );
}

function EmptyChart({ text }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-[#616161] border border-dashed border-[#E0E0E0] rounded-md">
      {text}
    </div>
  );
}
